<script setup>
import { useUserStore } from '@/stores/user'
import { computed, ref } from 'vue'
import ComboList from '@/components/Combo/List.vue'
import { useRouter } from 'vue-router'
import BetAmountInput from '@/components/BetAmountInput.vue'
import { bettingConfig } from '@/config/betting'
import PotentialWinning from '@/components/PotentialWinning.vue'
import { useI18n } from 'vue-i18n'
import { userBetsAPI } from '@/services/api'

const { t } = useI18n()
const userStore = useUserStore()
const router = useRouter()

const placingBet = ref(false)
const placedBet = ref(false)
const betAmount = ref(10)
const combo = computed(() => userStore.combo)
const invalid_outcome_ids = ref([])

const combinedPrice = computed(() => {
  if (!combo.value || combo.value.length === 0) return 0;
  return combo.value.reduce((total, item) => total * item.outcome.price, 1);
});

const removeItem = (index) => {
  userStore.removeFromCombo(index)
}

const isValidBetAmount = computed(() => {
  return betAmount.value && !betAmountError.value
})

const betAmountError = computed(() => {
  if (!betAmount.value) return null
  const amount = parseFloat(betAmount.value)
  if (isNaN(amount)) {
    return t('betting.validation.mustBeNumber')
  }
  if (amount <= 0) {
    return t('betting.validation.mustBePositive')
  }
  if (amount < bettingConfig.minAmount) {
    return t('betting.validation.minAmount', { min: bettingConfig.minAmount })
  }
  if (amount > bettingConfig.maxAmount) {
    return t('betting.validation.maxAmount', { max: bettingConfig.maxAmount })
  }
  return null
})

const potentialWinning = computed(() => {
  if (!combinedPrice.value || !isValidBetAmount.value) return 0
  return (betAmount.value * combinedPrice.value).toFixed(2)
})

const placeCombo = async () => {
  if (!userStore.isAuthenticated) {
    router.push({ name: 'login'})
    return
  }
  if (!isValidBetAmount.value) {
    return
  }
  placingBet.value = true
  try {
    const betData = {
      amount: parseFloat(betAmount.value),
      outcomes: combo.value.map(item => item.outcome.id),
    }
    const response = await userBetsAPI.placeCombo(betData)
    if (response.success) {
      betAmount.value = bettingConfig.defaultAmount
      placedBet.value = true
      userStore.resetCombo()
      router.push({ name: 'mybet', params: { id: response.data.id } })
    } else {
      console.error('Failed to place combo bet')
    }
  } catch (error) {
    // handle error as needed
    invalid_outcome_ids.value = error.response?.data?.invalid_outcome_ids || []
    alert(error.response?.data.message + '\n' + invalid_outcome_ids.value.join(', '))

  } finally {
    placingBet.value = false
  }
}
</script>

<template>
  <v-container>
    <h1 class="text-h4 font-weight-bold mb-4">{{ $t('combo.newCombo') }}</h1>
    
    <div v-if="combo && combo.length > 0">
      <ComboList :combo="combo"
        :invalid_outcome_ids="invalid_outcome_ids"
        @remove="removeItem"
      />

      <div class="text-uppercase text-subtitle-1 my-8">
        {{ $t('combo.totalOdds') }} <v-chip color="success" size="large" class="ms-1">{{ combinedPrice.toFixed(2) }}</v-chip>
      </div>
      
      <!-- v-row con sfondo scuro -->
      <v-row v-if="!placedBet" class="bg-grey-darken-4 pa-4">
        <v-col cols="12" sm="6">
          <BetAmountInput
            v-model="betAmount"
            :errorMessages="betAmountError"
          />
          <PotentialWinning 
            :potentialWinning="potentialWinning"
          />

          <v-btn
            color="success"
            size="large"
            block
            :disabled="!isValidBetAmount"
            :loading="placingBet"
            @click="placeCombo"
          >
            <v-icon class="mr-2">mdi-check</v-icon>
            {{ $t('events.betting.placeCombo') }}
          </v-btn>
          
        </v-col>
        <v-col cols="12" sm="6">
          <v-alert type="info" variant="tonal" :icon="false" @click="router.push({ name: 'list' })" class="cursor-pointer">
          <v-icon class="mr-2" icon="mdi-playlist-plus" />
          {{ $t('combo.addItemsTip') }}
          </v-alert>
        </v-col>
      </v-row>

      <div v-else>
        <v-alert type="success" class="my-4" variant="outlined">
          {{ $t('combo.betPlacedSuccess') }}
        </v-alert>
      </div>
    </div>

    <div v-else>
      <v-alert type="info" class="mb-4" variant="tonal">
        {{ $t('combo.noItemsMessage') }}
      </v-alert>
    </div>
  </v-container>
</template>

<style scoped>
.combo-card {
  border-radius: 10px;
}
.vs {
  margin: 0 6px;
  color: #9e9e9e;
}
.outcome-name, .sport-title {
  color: #b0bec5;
}
</style>
